import React, { useState, useEffect } from 'react';
import { Mail, FileText, Users, BarChart3, Plus, Edit, Trash2, Play, Pause, Calendar, Filter } from 'lucide-react';
import { useTranslation } from '../../i18n/hooks/useTranslation';
import { useAuth } from '../../contexts/AuthContext';
import { EmailTemplateBuilder } from './EmailTemplateBuilder';
import { CampaignManager } from './CampaignManager';
import { SegmentBuilder } from './SegmentBuilder';
import { CampaignCalendar } from './CampaignCalendar';
import { formatDate } from '../../utils/dateUtils';

type EDMView = 'dashboard' | 'templates' | 'campaigns' | 'segments' | 'calendar' | 'analytics';

interface EDMTemplate {
  _id: string;
  name: string;
  category: string;
  isActive: boolean;
  createdAt: string;
}

interface EDMCampaign {
  _id: string;
  name: string;
  type: string;
  status: string;
  totalSent: number;
  totalOpened: number;
  totalClicked: number;
}

interface EDMSegment {
  _id: string;
  name: string;
  estimatedSize?: number;
  isActive: boolean;
}

export const EDMManager: React.FC = () => {
  const { t } = useTranslation();
  const { user, isAuthenticated, isLoading: authLoading } = useAuth();
  const [activeView, setActiveView] = useState<EDMView>('dashboard');
  const [templates, setTemplates] = useState<EDMTemplate[]>([]);
  const [campaigns, setCampaigns] = useState<EDMCampaign[]>([]);
  const [segments, setSegments] = useState<EDMSegment[]>([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalTemplates: 0,
    activeCampaigns: 0,
    totalSent: 0,
    totalOpened: 0,
    totalClicked: 0
  });

  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';

  useEffect(() => {
    // Wait for auth to finish loading, then only fetch if authenticated
    if (authLoading) {
      return; // Still checking auth
    }
    
    if (isAuthenticated && user) {
      fetchData();
    } else {
      // Not authenticated - set empty state
      setTemplates([]);
      setCampaigns([]);
      setSegments([]);
      setLoading(false);
    }
  }, [isAuthenticated, user, authLoading]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      if (!token || !isAuthenticated || !user) {
        // No token or not authenticated - set empty state
        setTemplates([]);
        setCampaigns([]);
        setSegments([]);
        setLoading(false);
        return;
      }

      const headers = {
        'Authorization': `Bearer ${token}`
      };

      // Fetch with error handling - returns null on auth errors
      // Note: Browser will still log 401 errors in console, but we handle them gracefully
      const fetchWithErrorHandling = async (url: string): Promise<Response | null> => {
        try {
          const response = await fetch(url, { headers });
          // If 401/403, return null silently (AuthContext will handle auth)
          if (response.status === 401 || response.status === 403) {
            return null;
          }
          return response;
        } catch (error) {
          // Network errors - return null
          return null;
        }
      };

      const [templatesRes, campaignsRes, segmentsRes] = await Promise.all([
        fetchWithErrorHandling(`${apiUrl}/api/edm/templates`),
        fetchWithErrorHandling(`${apiUrl}/api/edm/campaigns`),
        fetchWithErrorHandling(`${apiUrl}/api/edm/segments`)
      ]);

      // Handle templates response
      if (templatesRes && templatesRes.ok) {
        try {
          const templatesData = await templatesRes.json();
          if (templatesData.success) {
            setTemplates(templatesData.data || []);
            setStats(prev => ({ ...prev, totalTemplates: (templatesData.data || []).length }));
          }
        } catch (e) {
          console.warn('Error parsing templates response:', e);
        }
      } else if (templatesRes && (templatesRes.status === 401 || templatesRes.status === 403)) {
        // Auth error - let AuthContext handle it, just set empty state
        setTemplates([]);
      }

      // Handle campaigns response
      if (campaignsRes && campaignsRes.ok) {
        try {
          const campaignsData = await campaignsRes.json();
          if (campaignsData.success) {
            const campaignsList = campaignsData.data || [];
            setCampaigns(campaignsList);
            const active = campaignsList.filter((c: EDMCampaign) => 
              ['active', 'scheduled'].includes(c.status)
            ).length;
            const totalSent = campaignsList.reduce((sum: number, c: EDMCampaign) => sum + (c.totalSent || 0), 0);
            const totalOpened = campaignsList.reduce((sum: number, c: EDMCampaign) => sum + (c.totalOpened || 0), 0);
            const totalClicked = campaignsList.reduce((sum: number, c: EDMCampaign) => sum + (c.totalClicked || 0), 0);
            
            setStats(prev => ({
              ...prev,
              activeCampaigns: active,
              totalSent,
              totalOpened,
              totalClicked
            }));
          }
        } catch (e) {
          console.warn('Error parsing campaigns response:', e);
        }
      } else if (campaignsRes && (campaignsRes.status === 401 || campaignsRes.status === 403)) {
        // Auth error - let AuthContext handle it, just set empty state
        setCampaigns([]);
      }

      // Handle segments response
      if (segmentsRes && segmentsRes.ok) {
        try {
          const segmentsData = await segmentsRes.json();
          if (segmentsData.success) {
            setSegments(segmentsData.data || []);
          }
        } catch (e) {
          console.warn('Error parsing segments response:', e);
        }
      } else if (segmentsRes && (segmentsRes.status === 401 || segmentsRes.status === 403)) {
        // Auth error - let AuthContext handle it, just set empty state
        setSegments([]);
      }
    } catch (error) {
      console.error('Error fetching EDM data:', error);
      // Set empty state on error
      setTemplates([]);
      setCampaigns([]);
      setSegments([]);
    } finally {
      setLoading(false);
    }
  };

  const views = [
    { id: 'dashboard' as EDMView, label: t('edm.dashboard.title') || 'Dashboard', icon: BarChart3 },
    { id: 'templates' as EDMView, label: t('edm.templates.title') || 'Templates', icon: FileText },
    { id: 'campaigns' as EDMView, label: t('edm.campaigns.title') || 'Campaigns', icon: Mail },
    { id: 'segments' as EDMView, label: t('edm.segments.title') || 'Segments', icon: Users },
    { id: 'calendar' as EDMView, label: t('edm.calendar.title') || 'Calendar', icon: Calendar },
    { id: 'analytics' as EDMView, label: t('edm.analytics.title') || 'Analytics', icon: BarChart3 }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">{t('common.loading') || 'Loading...'}</div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
          {t('edm.title') || 'Marketing'}
        </h1>
        <p className="text-gray-600 dark:text-gray-400">
          {t('edm.description') || 'Create and manage email campaigns to educate users and drive engagement'}
        </p>
      </div>

      {/* Navigation Tabs */}
      <div className="border-b border-gray-200 dark:border-gray-700 mb-6">
        <nav className="flex space-x-8">
          {views.map(view => {
            const Icon = view.icon;
            return (
              <button
                key={view.id}
                onClick={() => setActiveView(view.id)}
                className={`
                  flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm
                  ${activeView === view.id
                    ? 'border-accent-500 text-accent-600 dark:text-accent-400'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                  }
                `}
              >
                <Icon className="w-5 h-5" />
                <span>{view.label}</span>
              </button>
            );
          })}
        </nav>
      </div>

      {/* Dashboard View */}
      {activeView === 'dashboard' && (
        <div className="space-y-6">
          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {t('edm.stats.totalTemplates') || 'Total Templates'}
                  </p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                    {stats.totalTemplates}
                  </p>
                </div>
                <FileText className="w-8 h-8 text-accent-500" />
              </div>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {t('edm.stats.activeCampaigns') || 'Active Campaigns'}
                  </p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                    {stats.activeCampaigns}
                  </p>
                </div>
                <Mail className="w-8 h-8 text-success-500" />
              </div>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {t('edm.stats.totalSent') || 'Total Sent'}
                  </p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                    {stats.totalSent.toLocaleString()}
                  </p>
                </div>
                <BarChart3 className="w-8 h-8 text-purple-500" />
              </div>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {t('edm.stats.openRate') || 'Open Rate'}
                  </p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                    {stats.totalSent > 0
                      ? `${((stats.totalOpened / stats.totalSent) * 100).toFixed(1)}%`
                      : '0%'}
                  </p>
                </div>
                <BarChart3 className="w-8 h-8 text-primary-500" />
              </div>
            </div>
          </div>

          {/* Recent Templates */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                {t('edm.recentTemplates') || 'Recent Templates'}
              </h2>
              <button
                onClick={() => setActiveView('templates')}
                className="text-accent-600 dark:text-accent-400 hover:text-accent-700 dark:hover:text-accent-300 text-sm font-medium"
              >
                {t('common.viewAll') || 'View All'}
              </button>
            </div>
            <div className="p-6">
              {templates.slice(0, 5).length === 0 ? (
                <p className="text-gray-500 dark:text-gray-400 text-center py-4">
                  {t('edm.templates.noTemplates') || 'No templates found'}
                </p>
              ) : (
                <div className="space-y-3">
                  {templates.slice(0, 5).map(template => (
                    <div
                      key={template._id}
                      className="flex items-center justify-between p-3 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg"
                    >
                      <div>
                        <p className="font-medium text-gray-900 dark:text-white">{template.name}</p>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          {template.category} • {formatDate(template.createdAt)}
                        </p>
                      </div>
                      <span
                        className={`px-2 py-1 text-xs rounded ${
                          template.isActive
                            ? 'bg-success-100 text-success-800 dark:bg-success-900 dark:text-success-200'
                            : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                        }`}
                      >
                        {template.isActive ? t('common.active') || 'Active' : t('common.inactive') || 'Inactive'}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Recent Campaigns */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                {t('edm.recentCampaigns') || 'Recent Campaigns'}
              </h2>
              <button
                onClick={() => setActiveView('campaigns')}
                className="text-accent-600 dark:text-accent-400 hover:text-accent-700 dark:hover:text-accent-300 text-sm font-medium"
              >
                {t('common.viewAll') || 'View All'}
              </button>
            </div>
            <div className="p-6">
              {campaigns.slice(0, 5).length === 0 ? (
                <p className="text-gray-500 dark:text-gray-400 text-center py-4">
                  {t('edm.campaigns.noCampaigns') || 'No campaigns found'}
                </p>
              ) : (
                <div className="space-y-3">
                  {campaigns.slice(0, 5).map(campaign => (
                    <div
                      key={campaign._id}
                      className="flex items-center justify-between p-3 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg"
                    >
                      <div>
                        <p className="font-medium text-gray-900 dark:text-white">{campaign.name}</p>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          {campaign.type} • {t(`edm.campaigns.status.${campaign.status}`) || campaign.status} • 
                          {' '}{campaign.totalSent || 0} {t('edm.campaigns.sent') || 'sent'}
                        </p>
                      </div>
                      <span
                        className={`px-2 py-1 text-xs rounded ${
                          campaign.status === 'active'
                            ? 'bg-success-100 text-success-800 dark:bg-success-900 dark:text-success-200'
                            : campaign.status === 'scheduled'
                            ? 'bg-accent-100 text-accent-800 dark:bg-accent-900 dark:text-accent-200'
                            : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                        }`}
                      >
                        {t(`edm.campaigns.status.${campaign.status}`) || campaign.status}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Templates View */}
      {activeView === 'templates' && (
        <EmailTemplateBuilder onUpdate={fetchData} />
      )}

      {/* Campaigns View */}
      {activeView === 'campaigns' && (
        <CampaignManager onUpdate={fetchData} />
      )}

      {/* Segments View */}
      {activeView === 'segments' && (
        <SegmentBuilder onUpdate={fetchData} />
      )}

      {/* Calendar View */}
      {activeView === 'calendar' && (
        <CampaignCalendar />
      )}

      {/* Analytics View */}
      {activeView === 'analytics' && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            {t('edm.analytics.title') || 'Campaign Analytics'}
          </h2>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {t('edm.analytics.totalOpened') || 'Total Opens'}
                </p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {stats.totalOpened.toLocaleString()}
                </p>
              </div>
              <div className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {t('edm.analytics.totalClicked') || 'Total Clicks'}
                </p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {stats.totalClicked.toLocaleString()}
                </p>
              </div>
            </div>
            <div className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                {t('edm.analytics.clickRate') || 'Click Rate'}
              </p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {stats.totalOpened > 0
                  ? `${((stats.totalClicked / stats.totalOpened) * 100).toFixed(2)}%`
                  : '0%'}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
